/* Ombrance — atelier-print.js : panneau « Imprimer mon œuvre » (<atelier-print>). Formats compatibles et prix du
   catalogue fournis par l'API (/artworks/:id/print-options), puis ajout au panier du produit « Œuvre Atelier —
   impression » avec les propriétés de ligne du contrat (_atelier_artwork_id masquée, « Création » visible). */

(() => {
  const A = window.OmbranceAtelier;
  if (!A || customElements.get('atelier-print')) return;
  const { t } = A;
  const variantNumber = (v) => { const m = String(v).match(/(\d+)\s*$/); return m ? Number(m[1]) : v; };
  const DIMS = /(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)/i;

  class AtelierPrint extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.dialog = this.querySelector('dialog');
      this.refs = {};
      this.querySelectorAll('[data-ref]').forEach((el) => { this.refs[el.dataset.ref] = el; });
      this.options = [];
      document.addEventListener('atelier:print', (e) => this.open(e.detail || {}));
      this.refs['print-options'].addEventListener('change', () => this.updateButton());
      this.refs['print-add'].addEventListener('click', () => this.add());
      this.refs['print-wall'].addEventListener('click', () => {
        const detail = { ...this.detail, sizes: this.sizes(), trigger: this.detail?.trigger };
        this.dialog.close();
        document.dispatchEvent(new CustomEvent('atelier:wall', { detail }));
      });
    }

    label() {
      return t('property_value', { brand: A.config.brand || '', style: this.detail?.styleName || '' });
    }

    async open(detail) {
      const { artwork } = detail;
      if (!artwork) return;
      this.detail = detail;
      this.options = [];
      const img = this.refs['print-img'];
      img.src = A.sized(artwork.previewUrl, 240);
      if (artwork.width && artwork.height) { img.width = 120; img.height = Math.round(120 * artwork.height / artwork.width); }
      this.refs['print-name'].textContent = this.label();
      this.renderOptions([]);
      this.setStatus(A.config.loadingLabel || '');
      this.refs['print-add'].disabled = true;
      this.refs['print-wall'].hidden = false;
      A.openDialog(this.dialog, detail.trigger);
      try {
        const res = await A.api.printOptions(artwork.id);
        const list = (Array.isArray(res) ? res : res.options || []).map(A.normPrint).filter((o) => o.variantId);
        // Jeton d'appartenance signé par le serveur : sans lui, la commande part en vérification manuelle.
        this.printToken = (!Array.isArray(res) && res.printToken) || null;
        this.options = list;
        this.renderOptions(list);
        this.setStatus(list.length ? '' : ((!Array.isArray(res) && res.message) || t('print_none')));
        this.refs['print-wall'].hidden = !list.length;
        this.updateButton();
      } catch (err) {
        this.setStatus(t('print_error'), () => this.open(detail));
      }
    }

    renderOptions(list) {
      const box = this.refs['print-options'];
      box.querySelectorAll('.print-opt').forEach((el) => el.remove());
      list.forEach((option, i) => {
        const label = document.createElement('label');
        label.className = 'print-opt';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'atelier-print-variant';
        input.value = String(i);
        input.checked = i === 0;
        const name = document.createElement('span');
        name.className = 'print-opt__label';
        name.textContent = option.label;
        const price = document.createElement('span');
        price.className = 'print-opt__price';
        price.textContent = A.money(option.price);
        label.append(input, name, price);
        box.appendChild(label);
      });
    }

    selected() {
      const input = this.refs['print-options'].querySelector('input:checked');
      return input ? this.options[Number(input.value)] : null;
    }

    updateButton() {
      const option = this.selected();
      const button = this.refs['print-add'];
      button.disabled = !option;
      button.textContent = option ? t('print_add', { price: A.money(option.price) }) : (A.config.loadingLabel || '');
    }

    setStatus(message, retry) {
      const box = this.refs['print-status'];
      box.replaceChildren();
      if (message) box.append(message);
      if (retry) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'link text-small';
        button.textContent = A.config.retryLabel || '';
        button.addEventListener('click', retry, { once: true });
        box.append(' ', button);
      }
    }

    /* Formats (cm) pour la mise en situation : repris des libellés « 50 × 70 » */
    sizes() {
      const seen = new Set();
      return this.options.map((o) => {
        const m = `${o.label} ${o.format}`.match(DIMS);
        if (!m) return null;
        const w = parseFloat(m[1].replace(',', '.'));
        const h = parseFloat(m[2].replace(',', '.'));
        const key = `${w}x${h}`;
        if (seen.has(key)) return null;
        seen.add(key);
        return { label: o.label, w, h };
      }).filter(Boolean);
    }

    async add() {
      const option = this.selected();
      const artwork = this.detail?.artwork;
      if (!option || !artwork) return;
      const button = this.refs['print-add'];
      button.disabled = true;
      button.textContent = t('print_adding');
      const properties = { _atelier_artwork_id: artwork.id, [A.config.propertyLabel || 'Création']: this.label() };
      if (artwork.previewUrl) properties._atelier_preview = A.sized(artwork.previewUrl, 360);
      if (this.printToken) properties._atelier_token = this.printToken;
      const items = [{ id: variantNumber(option.variantId), quantity: 1, properties }];
      try {
        if (window.theme?.cart?.add) {
          this.dialog.close();
          await window.theme.cart.add(items);
        } else {
          const res = await fetch(A.config.cartAddUrl || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!res.ok) throw new Error(String(res.status));
          location.href = (window.theme?.routes?.cart) || '/cart';
        }
        A.track('artwork_add_to_cart', {
          artwork_id: artwork.id, variant_id: String(variantNumber(option.variantId)), format: option.label,
          value: option.price / 100, currency: A.config.currency || 'EUR'
        });
      } catch (e) {
        if (!this.dialog.open) A.openDialog(this.dialog, this.detail?.trigger);
        this.setStatus(t('print_cart_error'));
        this.updateButton();
      }
    }
  }

  customElements.define('atelier-print', AtelierPrint);
})();
